import { useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { ReactElement, useRef, useState } from 'react'

import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { FilePreview, InputFile, InputFileContainer } from 'components/v2/InputFile'
import Modal from 'components/v2/Modal'
import { Row } from 'components/v2/Row'
import { addDatasetToRoutingProject } from 'features/RoutingProjects/cache'
import { CREATE_ROUTING_PROJECT_DATASET } from 'features/RoutingProjects/mutations'
import { ICreateDatasetForm } from 'features/RoutingProjects/types'
import { getFileNameWithoutExtension, usePushFileToS3 } from 'utils/file'
import { schema } from './schema'
import { getCSVErrors, hasCSVError } from 'features/RoutingProjects/utils'

const messages = defineMessages({
  namePlaceholder: {
    id: 'routingProjects.create.dataset.name.placeholder',
    defaultMessage: 'Enter a name'
  }
})

interface ICreateDatasetModalProps {
  routingProjectId: string
  closeModal: () => void
}

export const CreateDatasetModal = ({ routingProjectId, closeModal }: ICreateDatasetModalProps): ReactElement | null => {
  const intl = useIntl()
  const pushFileToS3 = usePushFileToS3()
  const newDatasetRef = useRef<ICreateDatasetForm | null>(null)
  const [s3Error, setS3Error] = useState<string | null>(null)
  const [createDataset, { error, reset }] = useMutation(CREATE_ROUTING_PROJECT_DATASET, {
    onCompleted() {
      closeModal()
    },
    update(cache, { data }) {
      const newDataset = data?.createRoutingProjectDataset
      if (newDataset) {
        addDatasetToRoutingProject({
          routingProjectId,
          datasetId: newDataset.id,
          datasetName: newDatasetRef.current?.name || ''
        })
      }
    }
  })

  const handleSubmit = async (
    values: ICreateDatasetForm,
    { setSubmitting }: FormikHelpers<ICreateDatasetForm>
  ): Promise<void> => {
    let path = ''
    try {
      path = await pushFileToS3(values.file as File)
    } catch (error) {
      // @ts-expect-error FIXME
      setS3Error(error.message)
      setSubmitting(false)
      return
    }
    try {
      newDatasetRef.current = values
      await createDataset({
        variables: {
          input: {
            id: routingProjectId,
            dataset: {
              name: values.name,
              url: path
            }
          }
        }
      })
    } catch (ex) {
      // Do nothing
    }
    setSubmitting(false)
  }

  const initialValues: ICreateDatasetForm = {
    name: '',
    file: ''
  }

  return (
    <Modal.Container>
      <Modal.Header smallMarginBottom>
        <Heading.H5>
          <FormattedMessage id="routingProjects.create.dataset.title" defaultMessage="Upload Dataset" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { handleChange, setValues, handleBlur, touched, errors, values, isSubmitting } = props

          const handleFileChange = async (file: File | ''): Promise<void> => {
            reset()
            setS3Error(null)
            if (!file) {
              setValues({ ...values, file: '' })
              return
            } else {
              setValues({ ...values, file, name: getFileNameWithoutExtension(file) })
            }
          }

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <InputFileContainer>
                      <InputFile
                        touched={touched.file}
                        errors={errors.file}
                        onChange={handleFileChange}
                        accept={{
                          'text/csv': []
                        }}
                      />
                      {!isEmpty(values.file) && (
                        <FilePreview
                          file={values.file as File}
                          filename={(values.file as File).name}
                          onRemoveFile={(): void => {
                            reset()
                            setS3Error(null)
                            setValues({
                              ...values,
                              file: '',
                              name: ''
                            })
                          }}
                        />
                      )}
                    </InputFileContainer>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Input
                      id="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.name}
                      errors={errors.name}
                      label={<FormattedMessage id="routingProjects.create.dataset.name.label" defaultMessage="Name" />}
                      placeholder={intl.formatMessage(messages.namePlaceholder)}
                      disabled={isEmpty(values.file)}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {(error || s3Error) && !isSubmitting && (
                  <Modal.ErrorMessage>
                    {hasCSVError(error) ? (
                      <ErrorMessage>{getCSVErrors(error, intl)}</ErrorMessage>
                    ) : (
                      <ErrorMessage>
                        <FormattedMessage
                          id="routingProjects.create.dataset.error"
                          defaultMessage="An error occurred while uploading the dataset"
                        />
                      </ErrorMessage>
                    )}
                  </Modal.ErrorMessage>
                )}

                <Modal.Actions>
                  <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="routingProjects.create.dataset.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button kind="primary" loading={isSubmitting}>
                    <FormattedMessage id="routingProjects.create.dataset.submit" defaultMessage="Save" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
